import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Cpu, Atom } from 'lucide-react';

const technologies = [
  {
    icon: Brain,
    title: "Artificial Intelligence",
    description: "Pushing the boundaries of machine cognition with advanced neural networks and deep learning systems.",
    color: "from-[#FF2E63]",
    features: ["Neural Networks", "Deep Learning", "Natural Language Processing"]
  },
  {
    icon: Cpu,
    title: "Neural Interfaces",
    description: "Bridging the gap between human consciousness and digital realms through direct neural connections.",
    color: "from-[#08F7FE]",
    features: ["Brain-Computer Interface", "Neural Mapping", "Cognitive Enhancement"]
  },
  {
    icon: Atom,
    title: "Quantum Computing",
    description: "Harnessing quantum mechanics to solve complex problems beyond classical computing capabilities.",
    color: "from-[#B4FF39]",
    features: ["Quantum Algorithms", "Error Correction", "Quantum Encryption"]
  }
];

const Particles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {[...Array(50)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-white rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            scale: [0, 1, 0],
            opacity: [0, 0.5, 0],
          }}
          transition={{
            duration: Math.random() * 3 + 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
};

export default function Technologies() {
  return (
    <div id="technologies" data-avatar-target="technologies" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B] to-black">
        {/* Animated grid lines */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-px w-full bg-gradient-to-r from-transparent via-[#08F7FE]/20 to-transparent"
              style={{ top: `${i * 5}%` }}
              animate={{
                opacity: [0.2, 0.5, 0.2],
                scaleX: [0.95, 1, 0.95],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.1,
              }}
            />
          ))}
        </div>
        <Particles />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <motion.h2 
            className="text-6xl font-bold mb-6 bg-gradient-to-r from-[#08F7FE] via-[#ff2e63] to-[#B4FF39] text-transparent bg-clip-text"
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              backgroundSize: '200% auto',
            }}
          >
            Core Technologies
          </motion.h2>
          <p className="text-2xl text-gray-400 max-w-3xl mx-auto font-light">
            Our breakthrough technologies are reshaping the future of human-machine interaction
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          {technologies.map((tech, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ scale: 1.02, translateY: -5 }}
              className="relative group"
            >
              <div className="bg-[#121214]/80 backdrop-blur-xl rounded-2xl p-8 border border-gray-800 h-full">
                {/* Glowing background */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${tech.color} to-transparent opacity-0 group-hover:opacity-10 rounded-2xl transition-all duration-500 ease-out`}
                  animate={{
                    scale: [1, 1.02, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />

                {/* Icon wrapper with glow effect */}
                <div className="relative mb-8 w-16 h-16">
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-r ${tech.color} to-transparent rounded-full blur-xl opacity-40 group-hover:opacity-60 transition-opacity duration-300`}
                  />
                  <div className="relative flex items-center justify-center w-full h-full">
                    <tech.icon className={`h-10 w-10 ${tech.color.replace('from-', 'text-')}`} />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">{tech.title}</h3>
                <p className="text-gray-400 mb-8 leading-relaxed">{tech.description}</p>
                
                <div className="space-y-3">
                  {tech.features.map((feature, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + (i * 0.1) }}
                      className="flex items-center space-x-3 group/feature"
                    >
                      <motion.div 
                        className={`w-2 h-2 rounded-full bg-gradient-to-r ${tech.color} to-transparent group-hover/feature:scale-125 transition-transform duration-300`}
                        animate={{
                          scale: [1, 1.2, 1],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.2,
                        }}
                      />
                      <span className="text-gray-300 group-hover/feature:text-white transition-colors duration-300">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}